/* eslint-disable no-console */
/* eslint-disable no-underscore-dangle */
import axios, { AxiosRequestConfig, AxiosRequestHeaders, AxiosResponse, CancelToken } from 'axios';
import qs from 'qs';

import getRequestHeaders from 'components/Common/api/getRequestHeaders';

import { generateQueryString } from './ParameterUtilities';

const isHousecallRequest = (urlPath: string) => {
  let url;

  try {
    url = new URL(urlPath);
  } catch (error) {
    return true;
  }
  const isAbsoluteUrl = url.protocol === 'http:' || url.protocol === 'https:';
  return !isAbsoluteUrl;
};

const getDeviceContext = () => {
  const deviceContext = JSON.stringify({
    os: {
      name: 'Web',
    },
    userAgent: navigator.userAgent,
  });

  return btoa(deviceContext);
};

const paymentRequiredHandlers: Array<(response: AxiosResponse) => void> = [];

const handlePaymentRequired = (response: AxiosResponse) => {
  paymentRequiredHandlers.forEach((handler) => {
    handler(response);
  });
};

declare module 'amplitude-js' {
  export interface AmplitudeClient {
    _sessionId?: string;
  }
}

const newHttpClient = () => {
  const httpClient = axios.create();

  httpClient.interceptors.request.use((config) => {
    if (isHousecallRequest(config.url!)) {
      const sessionId = window.amplitude?.getInstance()?._sessionId;

      if (sessionId) {
        httpClient.defaults.headers.common['Amplitude-Session-Id'] = sessionId;
      }
      httpClient.defaults.headers.common['Segment-Device-Context'] = getDeviceContext();
    }
    return config;
  });

  httpClient.interceptors.response.use(
    (response) => {
      if (response.status === 402) {
        handlePaymentRequired(response);
      }

      return response;
    },
    (error) => {
      if (error.response && error.response.status === 402) {
        handlePaymentRequired(error.response);
      }

      return Promise.reject(error);
    }
  );

  return httpClient;
};

export const httpClient = newHttpClient();

const sendRequest = <ResponseObject = never, PayloadObject = never>(config: AxiosRequestConfig<PayloadObject>) =>
  httpClient.request<ResponseObject>(config);

const httpMethods = {
  GET: 'GET',
  POST: 'POST',
  DELETE: 'DELETE',
  PATCH: 'PATCH',
  PUT: 'PUT',
} as const;

const RestAPI = {
  getWithoutHeaders<ResponseObject = never>(requestUrl: string) {
    return sendRequest<ResponseObject>({
      url: requestUrl,
      method: httpMethods.GET,
    });
  },

  get<ResponseObject = never, PayloadObject = never>(requestUrl: string, options: any = {}) {
    return sendRequest<ResponseObject, PayloadObject>({
      url: requestUrl,
      headers: getRequestHeaders(),
      method: httpMethods.GET,
      ...options,
    });
  },

  getWithHeaders<ResponseObject = never, PayloadObject = never>(requestUrl: string, headers: AxiosRequestHeaders = {}) {
    return sendRequest<ResponseObject, PayloadObject>({
      url: requestUrl,
      headers: {
        ...getRequestHeaders(),
        ...headers,
      },
      method: httpMethods.GET,
    });
  },

  getWithFormData<ResponseObject = never, PayloadObject = never>(
    requestUrl: string,
    formData?: PayloadObject,
    cancelToken?: CancelToken,
    signal?: AbortSignal
  ) {
    return sendRequest<ResponseObject, PayloadObject>({
      url: requestUrl,
      headers: getRequestHeaders(),
      method: httpMethods.GET,
      params: formData,
      cancelToken,
      signal,
    });
  },

  getWithQueryString<ResponseObject = never, PayloadObject = never>(
    requestUrl: string,
    formData?: PayloadObject,
    headers: AxiosRequestHeaders = {}
  ) {
    return sendRequest<ResponseObject, PayloadObject>({
      url: requestUrl,
      headers: { ...getRequestHeaders(), ...headers },
      method: httpMethods.GET,
      params: formData,
      paramsSerializer(params) {
        return generateQueryString(params);
      },
    });
  },

  deleteWithFormData<ResponseObject = never, PayloadObject = never>(requestUrl: string, formData: PayloadObject) {
    return sendRequest<ResponseObject, PayloadObject>({
      url: requestUrl,
      headers: getRequestHeaders(),
      method: httpMethods.DELETE,
      data: formData,
    });
  },

  delete<ResponseObject = never, PayloadObject = never>(requestUrl: string) {
    return sendRequest<ResponseObject, PayloadObject>({
      url: requestUrl,
      headers: getRequestHeaders(),
      method: httpMethods.DELETE,
    });
  },

  deleteWithCustomHeaders<ResponseObject = never, PayloadObject = never>(
    requestUrl: string,
    requestHeaders: AxiosRequestHeaders,
    formData: PayloadObject,
    restConfigs: AxiosRequestConfig = {}
  ) {
    const normalHeaders = getRequestHeaders();
    const headers = {
      ...normalHeaders,
      ...requestHeaders,
    };

    return sendRequest<ResponseObject, PayloadObject>({
      url: requestUrl,
      headers,
      method: httpMethods.DELETE,
      data: formData,
      ...restConfigs,
    });
  },

  post<ResponseObject = never, PayloadObject = never>(requestUrl: string, options: any = {}) {
    return sendRequest<ResponseObject, PayloadObject>({
      url: requestUrl,
      headers: getRequestHeaders(),
      method: httpMethods.POST,
      ...options,
    });
  },

  postWithFormData<ResponseObject = never, PayloadObject = never>(
    requestUrl: string,
    formData?: PayloadObject,
    cancelToken?: CancelToken,
    signal?: AbortSignal
  ) {
    return sendRequest<ResponseObject, PayloadObject>({
      url: requestUrl,
      headers: getRequestHeaders(),
      method: httpMethods.POST,
      data: formData,
      cancelToken,
      signal,
    });
  },

  postAsUrlEncoded<ResponseObject = never, PayloadObject = never>(requestUrl: string, data: PayloadObject) {
    return sendRequest<ResponseObject, string>({
      url: requestUrl,
      method: httpMethods.POST,
      headers: {
        'content-type': 'application/x-www-form-urlencoded',
        'X-Requested-With': 'XMLHttpRequest',
      },
      data: qs.stringify(data),
    });
  },

  updateWithFormData<ResponseObject = never, PayloadObject = never>(
    requestUrl: string,
    formData: PayloadObject,
    options: any = {}
  ) {
    return sendRequest<ResponseObject, PayloadObject>({
      url: requestUrl,
      headers: getRequestHeaders(),
      method: httpMethods.PUT,
      data: formData,
      ...options,
    });
  },

  patchWithFormData<ResponseObject = never, PayloadObject = never>(requestUrl: string, formData: PayloadObject) {
    return sendRequest<ResponseObject, PayloadObject>({
      url: requestUrl,
      headers: getRequestHeaders(),
      method: httpMethods.PATCH,
      data: formData,
    });
  },

  onPaymentRequired(handler: (response: AxiosResponse) => void) {
    paymentRequiredHandlers.push(handler);
  },

  postWithCustomHeaders<ResponseObject = never, PayloadObject = never>(
    requestUrl: string,
    requestHeaders: AxiosRequestHeaders,
    formData: PayloadObject,
    restConfigs: AxiosRequestConfig = {}
  ) {
    const normalHeaders = getRequestHeaders();
    const headers = {
      ...normalHeaders,
      ...requestHeaders,
    };

    return sendRequest<ResponseObject, PayloadObject>({
      url: requestUrl,
      headers,
      method: httpMethods.POST,
      data: formData,
      ...restConfigs,
    });
  },

  putWithCustomHeaders<ResponseObject = never, PayloadObject = never>(
    requestUrl: string,
    requestHeaders: AxiosRequestHeaders,
    formData: PayloadObject,
    restConfigs: AxiosRequestConfig = {}
  ) {
    const normalHeaders = getRequestHeaders();
    const headers = {
      ...normalHeaders,
      ...requestHeaders,
    };

    return sendRequest<ResponseObject, PayloadObject>({
      url: requestUrl,
      headers,
      method: httpMethods.PUT,
      data: formData,
      ...restConfigs,
    });
  },
};

export default RestAPI;
